// src/pages/admin/components/BrandKitIntakeForm.jsx
import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../../lib/supabase'
import { STYLE_PRESET_OPTIONS } from '../../../lib/brandStylePresets'

const VIBE_OPTIONS = [
  'minimalist', 'warm', 'premium', 'playful', 'bold', 'organic',
  'corporate', 'futuristic', 'vintage', 'edgy', 'friendly', 'serious',
]

// Draft autosave — survives refreshes so admins don't lose long-form input.
// Scoped per client so different clients keep separate drafts. Also scoped by
// mode so an abandoned regenerate session can't pollute a later fresh-start
// flow on the same client (the regen prefill is much fuller than fresh-start
// defaults and would silently override them).
const draftKey = (clientId, mode) => `brand-kit-draft-${clientId}-${mode}`
const readDraft = (clientId, mode) => {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(draftKey(clientId, mode))
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}
const writeDraft = (clientId, mode, form) => {
  if (typeof window === 'undefined') return
  try { localStorage.setItem(draftKey(clientId, mode), JSON.stringify(form)) } catch { /* quota / private mode */ }
}
const clearDraft = (clientId, mode) => {
  if (typeof window === 'undefined') return
  try { localStorage.removeItem(draftKey(clientId, mode)) } catch { /* ignore */ }
}

const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(0,212,255,0.15)',
  borderRadius: 8, padding: '10px 12px',
  color: '#F1F5F9', fontFamily: '"Plus Jakarta Sans", sans-serif',
  fontSize: 13, outline: 'none',
}

export default function BrandKitIntakeForm({ client, linkedAudit, previousInputs, onStarted }) {
  const isRegenerate = !!previousInputs
  // On regenerate, lock the path to whatever the previous kit used — that
  // determines field requirements (business_description is required for
  // cold_start but not audit_prefill) and what we send to the server.
  const isPath1 = isRegenerate
    ? previousInputs.path === 'audit_prefill'
    : !!linkedAudit

  // Prefill from audit when present
  const auditInputs = linkedAudit?.inputs || {}

  // Re-shape the persisted brand_colors array ([{name,hex}, …]) back into the
  // form's per-role object ({primary, secondary, accent}).
  const prevColorsByRole = useMemo(() => {
    const out = { primary: '', secondary: '', accent: '' }
    if (Array.isArray(previousInputs?.brand_colors)) {
      for (const c of previousInputs.brand_colors) {
        if (c?.name && typeof c.hex === 'string') out[c.name] = c.hex
      }
    }
    return out
  }, [previousInputs])

  const initial = useMemo(() => {
    // Regenerate path wins over both the cold-start defaults and the audit
    // prefill — the admin explicitly chose to iterate on the previous kit.
    if (isRegenerate) {
      return {
        business_name: previousInputs.business_name || client.company || client.name || '',
        business_description: previousInputs.business_description || '',
        industry: previousInputs.industry || '',
        audience: previousInputs.audience || '',
        vibe: Array.isArray(previousInputs.vibe) ? previousInputs.vibe : [],
        color_preference: previousInputs.color_preference || '',
        inspirations: previousInputs.inspirations || '',
        voice_tone_preference: previousInputs.voice_tone_preference || '',
        goal: previousInputs.goal || '',
        challenge: previousInputs.challenge || '',
        style_preset: previousInputs.style_preset || 'auto',
        brand_colors: prevColorsByRole,
        existing_logo_url: previousInputs.existing_logo_url || '',
        website_url: previousInputs.website_url || '',
        imagery_direction: previousInputs.imagery_direction || '',
        tagline_override: previousInputs.tagline_override || '',
        cta_override: previousInputs.cta_override || '',
      }
    }
    return {
      business_name: client.company || client.name || '',
      business_description: '',
      industry: auditInputs.industry || '',
      audience: auditInputs.audience || '',
      vibe: [],
      color_preference: '',
      inspirations: '',
      voice_tone_preference: '',
      goal: auditInputs.goal || '',
      challenge: auditInputs.challenge || '',
      style_preset: 'auto',
      // Explicit hex picks; empty when the admin wants the AI to design a palette.
      brand_colors: { primary: '', secondary: '', accent: '' },
      // Optional URL to an existing logo — skips the 3-logo generation entirely.
      existing_logo_url: '',
      // Scrape-only: source URL for the autofill button below. Not sent as
      // part of the kit-generation payload in handleSubmit.
      website_url: '',
      // Optional scene/backdrop direction injected only into banner + profile-picture
      // image prompts (logos stay clean).
      imagery_direction: '',
      // Optional admin overrides for the tagline + CTA rendered ON banners.
      // Leave blank to let the AI generate them.
      tagline_override: '',
      cta_override: '',
    }
  }, [client, auditInputs, isRegenerate, previousInputs, prevColorsByRole])

  // Distinct draft slot per mode so an abandoned regen can't leak into a
  // later fresh-start (and vice versa). Refresh-safety stays intact within
  // each mode.
  const draftMode = isRegenerate ? 'regen' : 'fresh'

  // Hydrate from localStorage on first mount; merge over `initial` so any
  // prefilled audit fields still shine through if the draft was saved before.
  const [form, setForm] = useState(() => {
    const draft = readDraft(client.id, draftMode)
    return draft ? { ...initial, ...draft } : initial
  })
  const [draftRestored, setDraftRestored] = useState(() => !!readDraft(client.id, draftMode))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [logoUploading, setLogoUploading] = useState(false)
  const [logoUploadErr, setLogoUploadErr] = useState(null)
  const [autofilling, setAutofilling] = useState(false)
  const [autofillErr, setAutofillErr] = useState(null)

  // Autosave every form change. Cheap — small JSON, no debounce needed.
  useEffect(() => {
    writeDraft(client.id, draftMode, form)
  }, [client.id, draftMode, form])

  const discardDraft = () => {
    clearDraft(client.id, draftMode)
    setForm(initial)
    setDraftRestored(false)
  }

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }))
  const toggleVibe = (v) => setField('vibe', form.vibe.includes(v) ? form.vibe.filter(x => x !== v) : [...form.vibe, v])
  const setColor = (role, hex) => setForm(prev => ({ ...prev, brand_colors: { ...prev.brand_colors, [role]: hex } }))

  async function handleLogoFile(file) {
    if (!file) return
    if (file.size > 3 * 1024 * 1024) { setLogoUploadErr('File must be 3 MB or smaller.'); return }
    setLogoUploading(true); setLogoUploadErr(null)
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = reject; r.readAsDataURL(file)
      })
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/website?action=upload-asset', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session?.access_token ?? ''}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, contentType: file.type, data: dataUrl }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json.message || json.error || `Error ${res.status}`)
      setField('existing_logo_url', json.url)
    } catch (e) {
      setLogoUploadErr(e.message)
    } finally {
      setLogoUploading(false)
    }
  }

  async function handleAutofill() {
    const site = (form.website_url || '').trim()
    const logo = (form.existing_logo_url || '').trim()
    if (!site && !logo) { setAutofillErr('Enter a website URL or upload a logo first.'); return }
    setAutofilling(true); setAutofillErr(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/website?action=brand-autofill', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session?.access_token ?? ''}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ website_url: site || undefined, logo_url: logo || undefined }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json.message || json.error || `Error ${res.status}`)
      // Fill only fields the AI returned (non-empty); leave others as-is.
      for (const k of ['business_description', 'industry', 'audience', 'color_preference', 'imagery_direction', 'tagline_override', 'cta_override']) {
        if (json[k] && String(json[k]).trim()) setField(k, String(json[k]).trim())
      }
      const bc = json.brand_colors || {}
      for (const role of ['primary', 'secondary', 'accent']) {
        if (bc[role] && /^#[0-9a-fA-F]{6}$/.test(bc[role])) setColor(role, bc[role])
      }
    } catch (e) {
      setAutofillErr(e.message)
    } finally {
      setAutofilling(false)
    }
  }

  // Extract valid #RRGGBB hex picks; ignore partial input while typing.
  const validBrandColors = ['primary', 'secondary', 'accent']
    .map(name => ({ name, hex: form.brand_colors[name] }))
    .filter(c => /^#[0-9a-fA-F]{6}$/.test(c.hex))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    // Required field check (color_preference is required only when brand_colors is empty).
    const requiredColdStart = ['business_name', 'business_description', 'industry', 'audience', 'inspirations']
    const requiredAuditPrefill = ['business_name', 'industry', 'audience', 'inspirations']
    const required = isPath1 ? requiredAuditPrefill : requiredColdStart
    for (const f of required) {
      if (!form[f] || (Array.isArray(form[f]) && form[f].length === 0)) {
        setError(`Missing required field: ${f.replace(/_/g, ' ')}`)
        return
      }
    }
    if (form.vibe.length === 0) {
      setError('Pick at least one vibe descriptor')
      return
    }
    if (!form.color_preference.trim() && validBrandColors.length === 0) {
      setError('Either describe the color preference OR pick explicit brand colors below.')
      return
    }
    if (form.existing_logo_url && !/^https?:\/\//.test(form.existing_logo_url.trim())) {
      setError('Existing logo URL must start with http:// or https://')
      return
    }

    setSubmitting(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      // Strip the per-role hex object (sent as a validated array shape below) and
      // the raw website_url (re-added trimmed). website_url feeds BOTH the
      // autofill scrape AND the URL rendered under the logo on the cinematic
      // cover banners.
      const { brand_colors: _bc, website_url: _wu, ...formClean } = form
      const inputs = {
        path: isPath1 ? 'audit_prefill' : 'cold_start',
        ...formClean,
        style_preset: form.style_preset || 'auto',
        existing_logo_url: form.existing_logo_url.trim() || undefined,
        imagery_direction: form.imagery_direction.trim() || undefined,
        tagline_override: form.tagline_override.trim() || undefined,
        cta_override: form.cta_override.trim() || undefined,
        website_url: form.website_url.trim() || undefined,
        ...(validBrandColors.length > 0 ? { brand_colors: validBrandColors } : {}),
      }
      const res = await fetch('/api/start-brand-kit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token ?? ''}`,
        },
        body: JSON.stringify({
          client_id: client.id,
          source_audit_id: linkedAudit?.id || null,
          inputs,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || data.error || `Request failed: ${res.status}`)
      // Successful submit — wipe both draft slots for this client so neither
      // a stale fresh draft nor a stale regen draft pollutes a future session.
      clearDraft(client.id, 'fresh')
      clearDraft(client.id, 'regen')
      onStarted(data.kit_id)
    } catch (err) {
      setError(err.message || 'Something went wrong')
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {draftRestored && (
        <div style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#86EFAC', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1 }}>Draft restored from your last visit — keep editing or discard to start fresh.</span>
          <button type="button" onClick={discardDraft} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6, padding: '4px 10px', color: '#86EFAC', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>
            Discard draft
          </button>
        </div>
      )}

      {isPath1 && !isRegenerate && (
        <div style={{ background: 'rgba(0,207,255,0.08)', border: '1px solid rgba(0,207,255,0.25)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#00CFFF' }}>
          Linked to audit from {new Date(linkedAudit.created_at).toLocaleDateString()} — industry, audience, goal, challenge prefilled.
        </div>
      )}

      {isRegenerate && (
        <div style={{ background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.3)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#C4B5FD' }}>
          Iterating on your previous brand kit — every field is prefilled. Tweak what you want and submit to generate a fresh kit.
        </div>
      )}

      <Field label="Business name *">
        <input value={form.business_name} onChange={e => setField('business_name', e.target.value)} style={inputStyle} required />
      </Field>

      <Field label="Autofill from website + logo (optional)">
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            style={{ ...inputStyle, flex: 1 }}
            value={form.website_url}
            onChange={e => setField('website_url', e.target.value)}
            placeholder="https://the-clients-website.com"
          />
          <button type="button" onClick={handleAutofill} disabled={autofilling} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 14px', whiteSpace: 'nowrap',
            background: autofilling ? 'rgba(139,92,246,0.4)' : 'linear-gradient(135deg, #8B5CF6, #00D4FF)',
            border: 'none', borderRadius: 8, color: '#020817', fontSize: 12, fontWeight: 700,
            fontFamily: 'inherit', cursor: autofilling ? 'not-allowed' : 'pointer',
          }}>{autofilling ? 'Reading…' : '✨ Autofill'}</button>
        </div>
        <p style={{ color: '#64748B', fontSize: '11px', margin: '4px 0 0' }}>
          Reads the site + your uploaded logo and fills in the fields below. Review before generating.
          This URL is also rendered under the logo on the cinematic cover banners.
        </p>
        {autofillErr && <p style={{ color: '#FCA5A5', fontSize: '11px', margin: '4px 0 0' }}>{autofillErr}</p>}
      </Field>

      {!isPath1 && (
        <Field label="What does the business do? *">
          <textarea value={form.business_description} onChange={e => setField('business_description', e.target.value)} style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} placeholder="e.g. Small-batch artisan coffee roaster, retail + wholesale" />
        </Field>
      )}

      <Field label="Industry *">
        <input value={form.industry} onChange={e => setField('industry', e.target.value)} style={inputStyle} required />
      </Field>

      <Field label="Target audience *">
        <textarea value={form.audience} onChange={e => setField('audience', e.target.value)} style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} placeholder="Who's the ideal customer?" />
      </Field>

      <Field label="Brand vibe * (pick 1-3)">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {VIBE_OPTIONS.map(v => (
            <button key={v} type="button" onClick={() => toggleVibe(v)}
              style={{
                background: form.vibe.includes(v) ? 'rgba(0,212,255,0.15)' : 'rgba(255,255,255,0.04)',
                border: form.vibe.includes(v) ? '1px solid #00D4FF' : '1px solid rgba(255,255,255,0.08)',
                color: form.vibe.includes(v) ? '#00D4FF' : '#94A3B8',
                borderRadius: 100, padding: '4px 12px', fontSize: 12, cursor: 'pointer',
              }}>{v}</button>
          ))}
        </div>
      </Field>

      <Field label="Design style">
        <select value={form.style_preset || 'auto'} onChange={e => setField('style_preset', e.target.value)} style={inputStyle}>
          {STYLE_PRESET_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>

      <Field label="Color preference (description)">
        <input value={form.color_preference} onChange={e => setField('color_preference', e.target.value)} style={inputStyle} placeholder="e.g. Earthy with one bold accent" />
        <p style={{ color: '#475569', fontSize: 11, margin: '4px 0 0' }}>
          Free-text description for the AI to interpret. Required UNLESS you pick explicit hex codes below.
        </p>
      </Field>

      <Field label="Brand colors (optional — explicit hex)">
        <p style={{ color: '#94A3B8', fontSize: 11, margin: '0 0 8px' }}>
          Already know your brand colors? Pick them here and the AI will use them verbatim instead of designing a palette.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {['primary', 'secondary', 'accent'].map(role => (
            <div key={role} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ minWidth: 78, fontSize: 12, color: '#94A3B8', textTransform: 'capitalize' }}>{role}</span>
              <input
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(form.brand_colors[role]) ? form.brand_colors[role] : '#000000'}
                onChange={e => setColor(role, e.target.value)}
                style={{ width: 36, height: 28, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, background: 'transparent', cursor: 'pointer', padding: 0 }}
                aria-label={`${role} color picker`}
              />
              <input
                type="text"
                value={form.brand_colors[role]}
                onChange={e => setColor(role, e.target.value)}
                placeholder="#RRGGBB"
                spellCheck={false}
                style={{ ...inputStyle, flex: 1, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
              />
            </div>
          ))}
        </div>
      </Field>

      <Field label="Already have a logo? (optional)">
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            style={{ ...inputStyle, flex: 1 }}
            value={form.existing_logo_url}
            onChange={e => setField('existing_logo_url', e.target.value)}
            placeholder="https://... or upload →"
          />
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 12px', background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.3)', borderRadius: 8, color: '#00D4FF', fontSize: 12, fontWeight: 600, cursor: logoUploading ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
            {logoUploading ? 'Uploading…' : 'Upload'}
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" style={{ display: 'none' }} disabled={logoUploading} onChange={e => handleLogoFile(e.target.files?.[0])} />
          </label>
        </div>
        {logoUploadErr && <p style={{ color: '#FCA5A5', fontSize: '11px', margin: '4px 0 0' }}>{logoUploadErr}</p>}
        <p style={{ color: '#475569', fontSize: 11, margin: '4px 0 0' }}>
          When provided, we skip the 3-logo generation and use your logo directly. Banners are designed around it.
        </p>
      </Field>

      <Field label="Imagery direction (optional)">
        <textarea
          value={form.imagery_direction}
          onChange={e => setField('imagery_direction', e.target.value)}
          maxLength={500}
          style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
          placeholder="e.g. villa interiors, yachts on Miami Intracoastal, infinity pools, beach cabanas at golden hour"
        />
        <p style={{ color: '#475569', fontSize: 11, margin: '4px 0 0' }}>
          Scenes / backdrops to include in banners (NOT logos). Be concrete — "villa interiors, yachts, pools" beats "luxury vibes". {form.imagery_direction.length}/500
        </p>
      </Field>

      <Field label="Tagline override (optional)">
        <input
          value={form.tagline_override}
          onChange={e => setField('tagline_override', e.target.value)}
          maxLength={80}
          style={inputStyle}
          placeholder="e.g. Luxury Living, Seamlessly Managed"
        />
        <p style={{ color: '#475569', fontSize: 11, margin: '4px 0 0' }}>
          5–8 words rendered ON banners. Leave blank to let the AI generate one from the brand brief. {form.tagline_override.length}/80
        </p>
      </Field>

      <Field label="Call-to-action override (optional)">
        <input
          value={form.cta_override}
          onChange={e => setField('cta_override', e.target.value)}
          maxLength={24}
          style={inputStyle}
          placeholder="e.g. Book Your Stay"
        />
        <p style={{ color: '#475569', fontSize: 11, margin: '4px 0 0' }}>
          2–4 words rendered as a button on banners. Leave blank to auto-generate. {form.cta_override.length}/24
        </p>
      </Field>

      <Field label="Inspirations / brand references *">
        <input value={form.inspirations} onChange={e => setField('inspirations', e.target.value)} style={inputStyle} placeholder="e.g. Blue Bottle, Stumptown" />
      </Field>

      {!isPath1 && (
        <Field label="Voice/tone preference (optional)">
          <input value={form.voice_tone_preference} onChange={e => setField('voice_tone_preference', e.target.value)} style={inputStyle} placeholder="e.g. Knowledgeable but unpretentious" />
        </Field>
      )}

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: 10, color: '#FCA5A5', fontSize: 12 }}>
          {error}
        </div>
      )}

      <button type="submit" disabled={submitting} style={{
        background: submitting ? 'rgba(0,212,255,0.4)' : 'linear-gradient(135deg, #00D4FF, #0099CC)',
        color: '#020817', border: 'none', borderRadius: 8, padding: '10px 20px',
        fontWeight: 700, fontSize: 14, cursor: submitting ? 'not-allowed' : 'pointer',
        marginTop: 8,
      }}>
        {submitting ? 'Starting…' : 'Generate Brand Kit'}
      </button>
    </form>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', color: '#94A3B8', fontSize: 12, fontWeight: 500, marginBottom: 6 }}>
        {label}
      </label>
      {children}
    </div>
  )
}
